// Smart Sash Admin Panel - Authentication Models
// Authentication, authorization, and token-related types

import { BaseEntity } from './common.models';

/**
 * Login request payload
 */
export interface LoginRequest {
  login?: string | null;
  email?: string | null;
  password?: string | null;
}

/**
 * Refresh token request payload
 */
export interface RefreshTokenRequest {
  refreshToken?: string | null;
}

/**
 * Authentication tokens response
 */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

/**
 * Login response metadata used by the frontend auth flow.
 * Tokens are set as HttpOnly cookies by the server — no token payload here.
 */
export interface LoginResponse {
  requiresPasswordChange: boolean;
}

/**
 * Refresh token entity
 */
export interface RefreshToken extends BaseEntity {
  institutionId: string;
  userId: string;
  token: string;
  expiresAt: string;
  revokedAt?: string | null;
  isActive: boolean;
}

/**
 * Change password request
 */
export interface ChangePasswordRequest {
  /** Optional for forced password change flows that already have a valid session. */
  oldPassword?: string | null;
  newPassword?: string | null;
}

/**
 * Forgot password request
 */
export interface ForgotPasswordRequest {
  email: string;
  institutionId?: string | null;
}

/**
 * Reset password request
 */
export interface ResetPasswordRequest {
  code: string;
  newPassword: string;
}

/**
 * User verification request
 */
export interface VerifyUserRequest {
  email?: string | null;
  code?: string | null;
}

/**
 * Smart Sash user roles matching backend Role.cs definitions.
 * Values match the role names returned by the /auth/me endpoint.
 */
export enum UserRole {
  HawkinsAdmin = 'HawkinsAdmin',
  HawkinsInstaller = 'HawkinsInstaller',
  InstitutionAdmin = 'InstitutionAdmin',
  LabAdmin = 'LabAdmin',
  LabSupervisor = 'LabSupervisor',
  SashUser = 'SashUser',
}

/** Human-readable display names for roles */
export const UserRoleLabels: Record<UserRole, string> = {
  [UserRole.HawkinsAdmin]: 'Hawkins Admin',
  [UserRole.HawkinsInstaller]: 'Hawkins Installer',
  [UserRole.InstitutionAdmin]: 'Institution Admin',
  [UserRole.LabAdmin]: 'Lab Admin',
  [UserRole.LabSupervisor]: 'Lab Supervisor',
  [UserRole.SashUser]: 'Sash User',
};

/** Legacy single-letter role codes still accepted by older auth responses. */
const RoleCodeToEnum: Record<string, UserRole> = {
  H: UserRole.HawkinsAdmin,
  I: UserRole.HawkinsInstaller,
  T: UserRole.InstitutionAdmin,
  L: UserRole.LabAdmin,
  S: UserRole.LabSupervisor,
  U: UserRole.SashUser,
};

/**
 * Parse pipe-separated single-letter role codes (e.g. "H|T") into UserRole array.
 * Kept for compatibility with older auth responses.
 */
export function parseRoleCodes(rolesString: string | null | undefined): UserRole[] {
  if (!rolesString) return [];
  return rolesString
    .split('|')
    .map((code) => RoleCodeToEnum[code])
    .filter((role): role is UserRole => role !== undefined);
}

/**
 * Parse pipe-separated ID string into string array.
 * Kept for compatibility with older auth responses.
 */
export function parsePipeSeparatedIds(idsString: string | null | undefined): string[] {
  if (!idsString) return [];
  return idsString.split('|').filter(Boolean);
}

/**
 * Authentication state for the application
 */
export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  loading: boolean;
  error: string | null;
}

/**
 * Authenticated user information
 */
export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: UserRole[];
  permissions: string[];
  institutionId: string;
  institutionName?: string;
  assignedLabIds: string[];
  assignedDeviceIds: string[];
}

/**
 * Current user info returned by GET /api/Auth/me (v2)
 */
export interface CurrentUserInfo {
  institutionId: string;
  userId: string;
  login?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  institutionName?: string | null;
  isEnabled: boolean;
  email?: string | null;
  phoneNumber?: string | null;
  emergencyContact?: string | null;
  permissions: string[];
  accountId: string;
  sessionId?: string | null;
}

/**
 * Login by card request
 */
export interface LoginByCardRequest {
  rfIdTag?: string | null;
  deviceId?: string | null;
  institutionId?: string | null;
}

/**
 * Login by card response
 */
export interface LoginByCardResponse {
  sessionId: string;
  accessToken?: string | null;
  refreshToken?: string | null;
  expiresIn?: number | null;
  user?: RFIDAuthUser | null;
  needRfIdAssigment: boolean;
  needDeviceRegistration: boolean;
  needUserAuthorization: boolean;
  registrationLink?: string | null;
  status?: string | null;
  institutionId?: string | null;
}

/**
 * Registration request payload
 */
export interface RegistrationRequest {
  email: string;
  password: string;
  sessionId: string;
}

/**
 * Session validation request
 */
export interface ValidateSessionRequest {
  sessionId: string;
  institutionId: string;
}

/**
 * Session status data from API
 */
export interface SessionStatusData {
  sessionId: string;
  status: string;
  isActive: boolean;
  isExpired: boolean;
  expiresAt: string;
  accountId: string;
  deviceId: string;
  rfCardId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Session validation response
 */
export interface ValidateSessionResponse {
  isValid: boolean;
  errorMessage?: string;
  expiresAt?: string;
  sessionData?: SessionStatusData;
}

/**
 * RFID authentication request
 */
export interface RFIDAuthRequest {
  sessionId: string;
  login: string;
  password: string;
}

/**
 * RFID authentication response
 */
export interface RFIDAuthUser {
  institutionId: string;
  accountId: string;
  userId: string;
}

export interface RFIDAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: RFIDAuthUser;
}
